import { PulsePin } from '@umanforge/common';
import { forwardRef, ForwardedRef, useState, useEffect, ChangeEvent, KeyboardEvent } from 'react';


interface InputNumberProp {
  value: number;
  label?: string;
  min?: number;
  max?: number;
  required?: boolean;
  disabled?: boolean;
  /** Number of decimals to round on commit. If null, do not round. Default: 2 */
  decimalPlaces?: number | null;
  onChange?: ( value: number ) => void;
  onKeyEnterUp?: ( event: KeyboardEvent ) => void;
};

export const InputNumber = forwardRef( function InputNumber( { value, label, min, max, decimalPlaces = 2, onChange, onKeyEnterUp, required = false, disabled = false }: InputNumberProp, ref: ForwardedRef<HTMLInputElement> ) {
  const [ isFocus, setIsFocus ] = useState<boolean>( false );
  const [ inputValueStr, setInputValueStr ] = useState<string>( String( value ?? '' ) );

  useEffect( () => {

    if ( !isFocus ) {
      if ( value != null && !isNaN( value ) ) {
        const display = decimalPlaces != null ? ( Number( value ).toFixed( decimalPlaces ) ) : String( value );
        setInputValueStr( display );
      } else {
        setInputValueStr( '' );
      }
    }
  }, [ value, isFocus, decimalPlaces ] );


  return (
    <div className='flex flex-col gap-[2px] w-full'>
      <p className='flex gap-1 ml-1 items-center text-base'>
        {label}
      </p>
      <div className='relative'>
        <div
          className={
            'flex w-full h-9 items-center rounded-lg outline min-w-9 overflow-hidden ' +
            ( isFocus ? 'outline-2 outline-gray-base-dark bg-off-white ' : 'outline-1 outline-gray-base ' ) +
            ( disabled ? 'bg-stone-100 text-gray-600' : 'hover:outline-gray-base-dark hover:bg-off-white text-black' )
          }
        >
          <input
            type='text'
            inputMode='decimal'
            required={ required }
            disabled={ disabled }
            ref={ ref }
            value={ inputValueStr }
            className={
              'w-full h-full select-none caret-secondary bg-transparent outline-0 text-center ' +
              ( disabled ? 'cursor-default' : 'cursor-pointer' )
            }
            onFocus={ () => { setIsFocus( !disabled && true ); } }
            onChange={ ( event: ChangeEvent ) => {
              const raw = ( event.target as HTMLInputElement ).value;
              const normalized = raw.replace( ',', '.' );
              if ( normalized === '' ) {
                setInputValueStr( '' );
                return;
              }
              const decimals = decimalPlaces == null ? 6 : Math.max( 0, Math.min( 10, decimalPlaces ) );
              const re = new RegExp( `^-?\\d*(?:\\.\\d{0,${ decimals }})?$` );
              const match = re.test( normalized );
              if ( match ) {
                setInputValueStr( normalized );
              }
            } }
            onKeyUp={ event => {
              if ( event.key === 'Enter' ) {
                const raw = ( event.target as HTMLInputElement ).value.replace( ',', '.' );
                let result = parseFloat( raw );
                if ( isNaN( result ) ) { result = 0; }
                if ( min != null && result < min ) { result = min; }
                if ( max != null && result > max ) { result = max; }
                if ( decimalPlaces != null ) {
                  const factor = Math.pow( 10, Math.max( 0, Math.min( 10, decimalPlaces ) ) );
                  result = Math.round( result * factor ) / factor;
                }
                setInputValueStr( String( result ) );
                if ( onChange != undefined ) { onChange( result ); }
                if ( onKeyEnterUp != undefined ) { onKeyEnterUp( event ); }
                ( event.target as HTMLInputElement ).blur();
              }
            } }
            onBlur={ event => {
              const raw = ( event.target as HTMLInputElement ).value.replace( ',', '.' );
              let result = parseFloat( raw );
              if ( isNaN( result ) ) { result = 0; }
              if ( min != null && result < min ) { result = min; }
              if ( max != null && result > max ) { result = max; }
              if ( decimalPlaces != null ) {
                const factor = Math.pow( 10, Math.max( 0, Math.min( 10, decimalPlaces ) ) );
                result = Math.round( result * factor ) / factor;
              }
              setInputValueStr( String( result ) );
              if ( onChange != undefined ) { onChange( result ); }
              setIsFocus( false );
            } }
          />
        </div>
        <PulsePin isDisplay={ required && ( inputValueStr === '' || isNaN( Number( inputValueStr ) ) ) } isPulse={ isFocus } />
      </div>
    </div>
  );
} );